import * as React from 'react';

import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';

import dayjs from 'dayjs';

import useAxios from '../utils/useAxios';
import { SectionCard, EmptyState, currency, number } from './ui';
import { PaymentCell, itemLabel } from './salePayment';
import { TRUE_COLORS } from '../theme';

type LastReceiptsListProps = { k: number };

const LastReceiptsList = ({ k }: LastReceiptsListProps) => {
  const axios = useAxios();
  const [receipts, setReceipts] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [openId, setOpenId] = React.useState<number | null>(null);

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get('api/receipts/', { params: { topk: k } });
        setReceipts(Array.isArray(data) ? data : []);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    })();
  }, [axios, k]);

  return (
    <SectionCard title="Últimas ventas" subtitle={`Los ${k} recibos más recientes`} noPadding>
      {loading ? (
        <Stack spacing={1} sx={{ px: 3, pb: 3 }}>
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} height={44} />)}
        </Stack>
      ) : receipts.length === 0 ? (
        <EmptyState
          compact
          icon={<ReceiptLongOutlinedIcon />}
          title="Sin ventas todavía"
          description="Aquí verás cada recibo en cuanto se registre en caja."
        />
      ) : (
        <List disablePadding sx={{ maxHeight: 392, overflow: 'auto', px: 1.5, pb: 1.5 }}>
          {receipts.map((receipt: any) => {
            const open = openId === receipt.id;
            const items: any[] = receipt.items ?? [];
            return (
              <Box key={receipt.id} sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => setOpenId(open ? null : receipt.id)}
                  selected={open}
                  sx={{ py: 1.25, px: 1.5 }}
                >
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                      {currency(receipt.amount)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                      {dayjs(receipt.purchase_date).format('D MMM, HH:mm')}
                      {items.length > 0 && ` · ${items.length} ${items.length === 1 ? 'artículo' : 'artículos'}`}
                    </Typography>
                  </Box>
                  <Box sx={{ mr: 1, flexShrink: 0 }}><PaymentCell sale={receipt} /></Box>
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{ color: TRUE_COLORS.secondary, transition: 'transform 150ms', transform: open ? 'rotate(180deg)' : 'none' }}
                  />
                </ListItemButton>
                <Collapse in={open} timeout="auto" unmountOnExit>
                  <Table size="small" sx={{ my: 1, '& td': { fontSize: '0.8125rem', px: 1.5 } }}>
                    <TableBody>
                      {items.map((item: any, i: number) => (
                        <TableRow key={i} sx={{ '&:last-child td': { border: 0 } }}>
                          <TableCell sx={{ color: 'text.secondary', width: 48 }}>{number(item.quantity, 2)}×</TableCell>
                          <TableCell>{itemLabel(item)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {currency((Number(item.quantity) || 0) * (Number(item.price) || 0))}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Collapse>
              </Box>
            );
          })}
        </List>
      )}
    </SectionCard>
  );
};

export default LastReceiptsList;
